import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  Clock3,
  Columns3,
  LayoutDashboard,
  Puzzle,
  RefreshCcw,
  Waypoints,
} from 'lucide-react';

import { Button } from '@/ui/button';
import { Card, CardContent } from '@/ui/card';
import { Input } from '@/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select';
import { cn } from '@/lib/utils';

import { ErrorFallback } from 'smart-react';
import {
  DATE_RANGE_OPTIONS,
  REFRESH_INTERVAL,
  RF_DASHBOARD_TILELAYOUT,
} from '../../../../constants/applicationConstants';
import { getRFAnalytics } from '../Services/SmartRFService';

const DEFAULT_METRICS = {
  totalProfiles: 0,
  totalDevices: 0,
  totalConnections: 0,
  totalCommands: 0,
};

const DASHBOARD_FONT_FAMILY =
  'Geist, "Geist Fallback", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", "Liberation Sans", sans-serif';
const DESIGN_GUIDE = {
  cardPadding: 'p-5',
  labelClass:
    'text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground',
  bodyTextClass: 'text-[13px] leading-6 text-muted-foreground',
  metricValueClass:
    'text-[28px] font-semibold leading-none tracking-[-0.03em] text-foreground',
};

const safeParse = (value, fallback) => {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const formatDateToInputValue = (date) => date.toISOString().split('T')[0];

const formatDateToDisplayValue = (value) => {
  if (!value) {
    return 'Select date';
  }

  const parsedDate = new Date(`${value}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(parsedDate);
};

const getDefaultDateRange = () => {
  const today = new Date();
  const lastWeek = new Date(today);
  lastWeek.setDate(today.getDate() - 7);

  return {
    startDate: formatDateToInputValue(lastWeek),
    endDate: formatDateToInputValue(today),
  };
};

const getStoredDateFilter = () => {
  if (typeof window === 'undefined') {
    return {
      range: '7',
      dates: getDefaultDateRange(),
    };
  }

  const storedValue = safeParse(
    window.localStorage.getItem(`${RF_DASHBOARD_TILELAYOUT}_DATE_FILTER`),
    null,
  );

  return {
    range: storedValue?.range ?? '7',
    dates: storedValue?.dates ?? getDefaultDateRange(),
  };
};

const getStoredRefreshInterval = () => {
  if (typeof window === 'undefined') {
    return REFRESH_INTERVAL[1];
  }

  const storedValue = safeParse(
    window.localStorage.getItem(`${RF_DASHBOARD_TILELAYOUT}_REFRESH_INTERVAL`),
    null,
  );

  if (!storedValue) {
    return REFRESH_INTERVAL[1];
  }

  return (
    REFRESH_INTERVAL.find((option) => option.value === storedValue?.value) ||
    REFRESH_INTERVAL.find((option) => option.label === storedValue?.label) ||
    REFRESH_INTERVAL.find((option) => option.label === storedValue?.text) ||
    REFRESH_INTERVAL[1]
  );
};

const toNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const compactNumberFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const formatCompactNumber = (value) =>
  compactNumberFormatter.format(toNumber(value));

const formatMetricValue = (value) =>
  new Intl.NumberFormat('en-US').format(toNumber(value));

const formatRatio = (value, total) =>
  total > 0 ? `${Math.round((value / total) * 100)}%` : '0%';

const getProgressWidth = (value, total, minimum = 18) => {
  if (value <= 0 || total <= 0) {
    return '0%';
  }

  return `${clamp(Math.round((value / total) * 100), minimum, 100)}%`;
};

const DashboardSurface = ({ className, ...props }) => (
  <Card
    className={cn(
      'rounded-lg border border-border/40 bg-card shadow-none',
      className,
    )}
    {...props}
  />
);

const DashboardSummaryPill = ({ value, label, accent = false }) => (
  <div
    className={cn(
      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium',
      accent
        ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
        : 'border-border/60 bg-background/70 text-muted-foreground',
    )}
  >
    <span
      className={cn(
        'font-semibold',
        accent ? 'text-emerald-700 dark:text-emerald-300' : 'text-foreground',
      )}
    >
      {value}
    </span>
    <span>{label}</span>
  </div>
);

const DashboardDateField = ({ label, value, onChange }) => (
  <label className='flex min-w-[180px] flex-col gap-2'>
    <span className='text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'>
      {label}
    </span>
    <div className='relative'>
      <div className='flex h-10 items-center gap-3 rounded-xl border border-border/60 bg-background/80 px-4 text-foreground shadow-none'>
        <CalendarDays className='h-4 w-4 shrink-0 text-muted-foreground' />
        <span className='text-[14px] font-medium'>
          {formatDateToDisplayValue(value)}
        </span>
      </div>
      <Input
        type='date'
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className='absolute inset-0 h-10 w-full cursor-pointer border-0 bg-transparent opacity-0 shadow-none [color-scheme:light] dark:[color-scheme:dark]'
      />
    </div>
  </label>
);

const MetricCardSkeleton = () => (
  <DashboardSurface className='overflow-hidden rounded-lg border-border/40 bg-card'>
    <CardContent className={DESIGN_GUIDE.cardPadding}>
      <div className='flex items-start justify-between gap-4'>
        <div className='space-y-3'>
          <div className='h-3 w-24 animate-pulse rounded bg-muted' />
          <div className='h-8 w-20 animate-pulse rounded bg-muted' />
        </div>
        <div className='h-11 w-11 animate-pulse rounded-lg bg-muted' />
      </div>
      <div className='mt-4 h-3 w-40 animate-pulse rounded bg-muted' />
      <div className='mt-5 h-1.5 animate-pulse rounded-full bg-muted' />
    </CardContent>
  </DashboardSurface>
);

const StatCard = ({
  icon: Icon,
  label,
  value,
  helper,
  onClick,
  accentClassName,
  progressClassName,
  progressWidth,
}) => (
  <button
    type='button'
    onClick={onClick}
    className='w-full appearance-none border-0 bg-transparent p-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60'
  >
    <DashboardSurface className='relative h-full overflow-hidden rounded-lg border-border/40 bg-card transition-colors hover:border-border/60 hover:bg-card/95'>
      <div className='absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-foreground/15 to-transparent opacity-70 dark:via-white/40' />
      <CardContent className={DESIGN_GUIDE.cardPadding}>
        <div className='flex items-start justify-between gap-4'>
          <div className='space-y-3'>
            <p className={DESIGN_GUIDE.labelClass}>{label}</p>
            <div className={DESIGN_GUIDE.metricValueClass}>{value}</div>
          </div>
          <span
            className={cn(
              'inline-flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 shadow-sm dark:border-border/45 dark:bg-background/55 dark:shadow-none',
              accentClassName,
            )}
          >
            <Icon className='h-5 w-5' />
          </span>
        </div>
        <p className={cn('mt-3', DESIGN_GUIDE.bodyTextClass)}>{helper}</p>
        <div className='mt-5 h-1.5 overflow-hidden rounded-full bg-border/35'>
          <div
            className={cn(
              'h-full rounded-full bg-gradient-to-r transition-[width] duration-700',
              progressClassName,
            )}
            style={{ width: progressWidth }}
          />
        </div>
      </CardContent>
    </DashboardSurface>
  </button>
);

const SmartRFDashboard = () => {
  const navigate = useNavigate();
  const storedDateFilter = React.useMemo(() => getStoredDateFilter(), []);
  const storedInterval = React.useMemo(() => getStoredRefreshInterval(), []);

  const [selectedRange, setSelectedRange] = React.useState(storedDateFilter.range);
  const [dates, setDates] = React.useState(storedDateFilter.dates);
  const [selectedInterval, setSelectedInterval] = React.useState(storedInterval);
  const [refreshTick, setRefreshTick] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [metrics, setMetrics] = React.useState(DEFAULT_METRICS);

  const updateSelectedDate = React.useCallback((nextDates, range) => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(
      `${RF_DASHBOARD_TILELAYOUT}_DATE_FILTER`,
      JSON.stringify({ range, dates: nextDates }),
    );
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(
      `${RF_DASHBOARD_TILELAYOUT}_REFRESH_INTERVAL`,
      JSON.stringify(selectedInterval),
    );
  }, [selectedInterval]);

  React.useEffect(() => {
    if (selectedInterval?.value === 'off') {
      return undefined;
    }

    const intervalInMinutes = Number.parseInt(selectedInterval?.value, 10);
    if (!Number.isFinite(intervalInMinutes)) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setRefreshTick((previousValue) => previousValue + 1);
    }, intervalInMinutes * 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [selectedInterval]);

  React.useEffect(() => {
    let ignore = false;

    const loadDashboardData = async () => {
      setIsLoading(true);

      try {
        const response = await getRFAnalytics({
          moduleName: 'SmartRFSummary',
          isServiceWorker: false,
          data: {},
        });

        if (ignore) {
          return;
        }

        setMetrics({
          totalProfiles: toNumber(response?.TotalProfiles),
          totalDevices: toNumber(response?.TotalDevices),
          totalConnections: toNumber(response?.TotalConnections),
          totalCommands: toNumber(response?.InfoCommands),
        });
      } catch (error) {
        if (!ignore) {
          console.error('Error loading SmartRF dashboard:', error);
          setMetrics(DEFAULT_METRICS);
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    void loadDashboardData();

    return () => {
      ignore = true;
    };
  }, [dates.endDate, dates.startDate, refreshTick]);

  const updateDateField = (field, value) => {
    const nextDates = {
      ...dates,
      [field]: value,
    };

    setSelectedRange('custom');
    setDates(nextDates);
    updateSelectedDate(nextDates, 'custom');
  };

  const handleRangeChange = (value) => {
    setSelectedRange(value);

    if (value === 'custom') {
      updateSelectedDate(dates, value);
      return;
    }

    const today = new Date();
    const nextStartDate = new Date(today);

    if (value === '14') {
      nextStartDate.setDate(today.getDate() - 14);
    } else if (value === '30') {
      nextStartDate.setDate(today.getDate() - 30);
    } else {
      nextStartDate.setDate(today.getDate() - 7);
    }

    const nextDates = {
      startDate: formatDateToInputValue(nextStartDate),
      endDate: formatDateToInputValue(today),
    };

    setDates(nextDates);
    updateSelectedDate(nextDates, value);
  };

  const handleIntervalChange = (value) => {
    const nextInterval =
      REFRESH_INTERVAL.find((option) => option.value === value) ||
      selectedInterval;

    setSelectedInterval(nextInterval);
  };

  const totalProfiles = toNumber(metrics.totalProfiles);
  const totalDevices = toNumber(metrics.totalDevices);
  const totalConnections = toNumber(metrics.totalConnections);
  const totalCommands = toNumber(metrics.totalCommands);

  const selectedRangeLabel =
    DATE_RANGE_OPTIONS.find((option) => option.value === selectedRange)?.text ||
    'Last 7 Days';
  const selectedRangeControlLabel =
    selectedRange === 'custom' ? 'Custom Range' : selectedRangeLabel;

  const connectionCoverage = totalDevices > 0
    ? formatRatio(totalConnections, totalDevices)
    : '0%';
  const commandCoverage = totalProfiles > 0
    ? formatRatio(totalCommands, totalProfiles)
    : '0%';

  const statCards = [
    {
      icon: LayoutDashboard,
      label: 'Profile setups',
      value: formatMetricValue(totalProfiles),
      helper:
        totalProfiles > 0
          ? `${formatCompactNumber(totalDevices)} enrolled devices tracked from active profile groups`
          : 'Create SmartRF profile groups to start building out device coverage.',
      accentClassName: 'text-sky-600 dark:text-sky-300',
      progressClassName: 'from-sky-400 via-cyan-400 to-primary/80',
      progressWidth: getProgressWidth(totalProfiles, Math.max(totalDevices, totalProfiles)),
      onClick: () =>
        navigate('/smartrf/profile-groups', { state: { isBack: true } }),
    },
    {
      icon: Columns3,
      label: 'Devices',
      value: formatMetricValue(totalDevices),
      helper:
        totalDevices > 0
          ? `${connectionCoverage} of enrolled devices currently represented in connection totals`
          : 'Enroll devices to populate the SmartRF workspace and connection metrics.',
      accentClassName: 'text-violet-600 dark:text-violet-300',
      progressClassName: 'from-violet-400 via-violet-400 to-primary/80',
      progressWidth: getProgressWidth(totalDevices, Math.max(totalProfiles, totalDevices)),
      onClick: () =>
        navigate('/smartrf/device-enrollment', { state: { isBack: true } }),
    },
    {
      icon: Waypoints,
      label: 'Connections',
      value: formatMetricValue(totalConnections),
      helper:
        totalConnections > 0
          ? `${formatCompactNumber(totalConnections)} device connections recorded across the RF fleet`
          : 'Connection activity will appear here once enrolled devices begin reporting.',
      accentClassName: 'text-cyan-600 dark:text-cyan-300',
      progressClassName: 'from-cyan-400 via-emerald-400 to-primary/80',
      progressWidth: getProgressWidth(totalConnections, Math.max(totalDevices, totalConnections)),
      onClick: () =>
        navigate('/smartrf/device-enrollment', { state: { isBack: true } }),
    },
    {
      icon: Puzzle,
      label: 'Info commands',
      value: formatMetricValue(totalCommands),
      helper:
        totalCommands > 0
          ? `${commandCoverage} command density compared with current profile setup volume`
          : 'Info commands stay available through the shared commands catalog.',
      accentClassName: 'text-amber-600 dark:text-amber-300',
      progressClassName: 'from-amber-400 via-amber-400 to-violet-400',
      progressWidth: getProgressWidth(totalCommands, Math.max(totalProfiles, totalCommands)),
      onClick: () => navigate('/autest/commands', { state: { isBack: true } }),
    },
  ];

  const topSummaryPills = [
    { value: selectedRangeControlLabel, label: 'range' },
    {
      value: formatCompactNumber(totalDevices),
      label: 'devices',
      accent: totalDevices > 0,
    },
    {
      value: formatCompactNumber(totalConnections),
      label: 'connections',
      accent: totalConnections > 0,
    },
    {
      value: selectedInterval?.text || selectedInterval?.label || 'Off',
      label: 'refresh',
    },
  ];

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div
        className='tw dashboard-theme'
        style={{ fontFamily: DASHBOARD_FONT_FAMILY }}
      >
        <div className='tw-container flex w-full flex-col gap-5 py-5 text-[13px] text-foreground sm:py-6 [&_a]:cursor-pointer [&_button]:cursor-pointer'>
          <section className='overflow-visible pt-1 text-foreground'>
            <div className='w-full space-y-1'>
              <div className='!mb-0 !text-[30px] font-medium leading-none tracking-tight text-foreground sm:!text-[32px]'>
                Dashboard
              </div>
              <p className='!mt-3 max-w-3xl text-[13px] leading-6 text-muted-foreground'>
                Track SmartRF profile setups, enrolled devices, connection
                activity, and info-command coverage from one workspace.
              </p>
            </div>

            <div className='mt-6 flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between'>
              <div className='min-w-0 space-y-4'>
                <div className='flex flex-wrap items-center gap-2'>
                  {topSummaryPills.map((item) => (
                    <DashboardSummaryPill key={item.label} {...item} />
                  ))}
                </div>
              </div>

              <div className='flex w-full flex-col gap-3 xl:ml-auto xl:w-auto xl:items-end'>
                {selectedRange === 'custom' ? (
                  <div className='grid w-full gap-3 sm:grid-cols-2 sm:justify-end xl:w-auto'>
                    <DashboardDateField
                      label='Start Date'
                      value={dates.startDate}
                      onChange={(value) => updateDateField('startDate', value)}
                    />

                    <DashboardDateField
                      label='End Date'
                      value={dates.endDate}
                      onChange={(value) => updateDateField('endDate', value)}
                    />
                  </div>
                ) : null}

                <div className='grid w-full grid-cols-[minmax(0,1fr)_40px_40px] gap-2 sm:ml-auto sm:w-fit sm:grid-cols-[220px_40px_40px] xl:items-end'>
                  <label className='flex min-w-0 flex-col gap-2 xl:min-w-[220px]'>
                    {selectedRange === 'custom' ? (
                      <span className='text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'>
                        Range
                      </span>
                    ) : (
                      <span className='h-[18px]' />
                    )}
                    <Select value={selectedRange} onValueChange={handleRangeChange}>
                      <SelectTrigger className='h-10 rounded-xl border-border/60 bg-background/80 px-4 text-[14px] text-foreground shadow-none [&>svg]:text-muted-foreground'>
                        <div className='flex min-w-0 items-center gap-3'>
                          <CalendarDays className='h-4 w-4 shrink-0 text-muted-foreground' />
                          <SelectValue placeholder='Select range'>
                            {selectedRangeControlLabel}
                          </SelectValue>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {DATE_RANGE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.value === 'custom' ? 'Custom Range' : option.text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>

                  <label className='flex min-w-[40px] self-end flex-col gap-2'>
                    <span className='h-[18px]' aria-hidden='true' />
                    <Select
                      value={selectedInterval?.value ?? 'off'}
                      onValueChange={handleIntervalChange}
                    >
                      <SelectTrigger
                        aria-label={`Refresh cadence: ${selectedInterval?.text || selectedInterval?.label || 'Off'}`}
                        className='h-10 w-10 justify-center rounded-xl border-border/60 bg-background/80 px-0 text-foreground shadow-none [&>svg:last-child]:hidden [&>svg]:text-muted-foreground'
                      >
                        <div className='flex min-w-0 items-center justify-center'>
                          <Clock3 className='h-4 w-4 shrink-0 text-muted-foreground' />
                          <span className='sr-only'>
                            {selectedInterval?.text || selectedInterval?.label || 'Off'}
                          </span>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {REFRESH_INTERVAL.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.text || option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>

                  <Button
                    type='button'
                    variant='outline'
                    size='icon'
                    onClick={() => setRefreshTick((previousValue) => previousValue + 1)}
                    className='h-10 w-10 self-end rounded-xl border-border/60 bg-background/80 text-foreground shadow-none hover:bg-accent hover:text-foreground'
                    aria-label='Refresh dashboard'
                  >
                    <RefreshCcw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section className='grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4'>
            {isLoading
              ? Array.from({ length: 4 }).map((_, index) => (
                <MetricCardSkeleton key={index} />
              ))
              : statCards.map((item) => <StatCard key={item.label} {...item} />)}
          </section>

          {isLoading ? (
            <div className='pointer-events-none fixed inset-x-0 bottom-6 z-10 flex justify-center'>
              <div className='rounded-full border border-border/70 bg-card px-4 py-2 shadow-lg'>
                <div className='flex items-center gap-2 text-[13px] text-muted-foreground'>
                  <RefreshCcw className='h-4 w-4 animate-spin' />
                  <span>Refreshing SmartRF dashboard</span>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default SmartRFDashboard;
